import { useReducer, useEffect, useRef, useState } from 'react'
import { wf, saveViewState } from '../outline/state.js'
import { ctx } from '../outline/ctx.js'
import { DISCIPLINE_ORDER, getDiscipline } from './DrawingSheetList.jsx'
import { MilestoneRow, PhaseRow } from './ScheduleSection.jsx'
import { updatePhaseTimeEntries } from '../services/phases.js'
import { getProjectSheets } from '../services/sheets.js'
import { getProjectBimSystems } from '../services/bim.js'
import { getProjectPhases, getProjectMilestones } from '../services/projects.js'
import { getActiveProjectHoldIds } from '../services/projectHold.js'
import { getProjectScope, setProjectScope } from '../outline/projectScope.js'
import { flightsForMember, crewIndexReady, crewIndexFailed } from '../outline/crew.js'

/* Format ISO date string "2026-06-15" → "Jun 15" (no timezone shift) */
function fmtDue(iso) {
  if (!iso) return null
  const parts = iso.split('-')
  if (parts.length < 3) return null
  const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec']
  return months[parseInt(parts[1], 10) - 1] + ' ' + parseInt(parts[2], 10)
}

/* ── Discipline grouping for raw Airtable sheet records ─────────────────── */
function groupByDiscipline(sheets) {
  const map = {}
  sheets.forEach(s => {
    const d = getDiscipline(s.fields['Sheet Number and Title'] || '')
    ;(map[d] = map[d] || []).push(s)
  })
  return DISCIPLINE_ORDER.filter(d => map[d]).map(d => ({ discipline: d, sheets: map[d] }))
}

// BIM Systems (BimNavItem, live count + expandable) renders BETWEEN Concepts and Coordination —
// split around it rather than a single flat list.
const DESIGN_SECTIONS_BEFORE_BIM = [
  { label: 'Criteria',       key: 'sec:design:criteria' },
  { label: 'Parcels',        key: 'sec:design:parcels' },
  { label: 'Concepts',       key: 'sec:design:concepts' },
]
const DESIGN_SECTIONS_AFTER_BIM = [
  { label: 'Coordination',   key: 'sec:design:coord' },
  // Specifications moved to Production (Round 12.5) — not a Design section zoom.
]

/* ── Design nav link (static sub-section links) ─────────────────────────── */
function DesignNavLink({ projectId }) {
  const key = 'design:' + projectId
  const isCollapsed = wf.collapsed[key] !== false

  return (
    <div className={`wf-node wf-node--sec-link${isCollapsed ? ' wf-node--collapsed' : ''}`} data-key={key}>
      <div className="wf-row">
        <button className="wf-triangle" data-fn="toggleCollapse" data-key={key}>
          <span className={`wf-tri-icon ${isCollapsed ? 'wf-tri-icon--right' : 'wf-tri-icon--down'}`}></span>
        </button>
        <button
          className="wf-bullet wf-bullet--sec-link"
          title="Open Design"
          data-fn="zoomTo"
          data-level="project-root"
          data-projid={projectId}
          data-rootname="design"
        ></button>
        <div className="wf-text-wrap">
          <span className="wf-node-text wf-node-text--sec-link">Design</span>
        </div>
        <button
          className="wf-menu-btn"
          data-fn="openMenu"
          data-type="project-nav"
          data-key={key}
          data-nodekey={key}
        >&#8943;</button>
      </div>
      {!isCollapsed && (
        <div className="wf-children">
          {DESIGN_SECTIONS_BEFORE_BIM.map(s => (
            <div key={s.key} className="wf-node wf-node--sec-link">
              <div className="wf-row">
                <span className="wf-triangle-spacer wf-triangle-spacer--hidden"></span>
                <button
                  className="wf-bullet wf-bullet--sec-link"
                  title={`Open ${s.label}`}
                  data-fn="zoomTo"
                  data-level="project-root"
                  data-projid={projectId}
                  data-rootname="design"
                  data-section={s.key}
                ></button>
                <div className="wf-text-wrap">
                  <span className="wf-node-text wf-node-text--sec-link">{s.label}</span>
                </div>
              </div>
            </div>
          ))}
          <BimNavItem projectId={projectId} />
          {DESIGN_SECTIONS_AFTER_BIM.map(s => (
            <div key={s.key} className="wf-node wf-node--sec-link">
              <div className="wf-row">
                <span className="wf-triangle-spacer wf-triangle-spacer--hidden"></span>
                <button
                  className="wf-bullet wf-bullet--sec-link"
                  title={`Open ${s.label}`}
                  data-fn="zoomTo"
                  data-level="project-root"
                  data-projid={projectId}
                  data-rootname="design"
                  data-section={s.key}
                ></button>
                <div className="wf-text-wrap">
                  <span className="wf-node-text wf-node-text--sec-link">{s.label}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

/* ── Discipline group in the project list — collapse persisted via wf ────── */
function DisciplineNavGroup({ discipline, sheets, projectId }) {
  const colKey = 'list-disc:' + discipline + ':' + projectId
  const isCollapsed = wf.collapsed[colKey] !== false

  return (
    <div className={`wf-node wf-node--sec-link${isCollapsed ? ' wf-node--collapsed' : ''}`} data-key={colKey}>
      <div className="wf-row">
        <button className="wf-triangle" data-fn="toggleCollapse" data-key={colKey}>
          <span className={`wf-tri-icon ${isCollapsed ? 'wf-tri-icon--right' : 'wf-tri-icon--down'}`}></span>
        </button>
        <button className="wf-bullet wf-bullet--sec-link" title={discipline}></button>
        <div className="wf-text-wrap">
          <span className="wf-node-text wf-node-text--sec-link">{discipline}</span>
          <span className="wf-prog" style={{ color: '#7a9aaa', marginLeft: 6 }}>{sheets.length}</span>
        </div>
      </div>
      {!isCollapsed && (
        <div className="wf-children">
          {sheets.map(s => (
            <div key={s.id} className="wf-node wf-node--sec-link">
              <div className="wf-row">
                <span className="wf-triangle-spacer wf-triangle-spacer--hidden"></span>
                <button className="wf-bullet wf-bullet--sec-link" title={s.fields['Sheet Number and Title'] || ''}></button>
                <div className="wf-text-wrap">
                  <span className="wf-node-text wf-node-text--sec-link">{s.fields['Sheet Number and Title'] || 'Untitled'}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

/* ── Drawing Sheets nav item — lazy loads on expand ─────────────────────── */
function DrawingSheetsNavItem({ projectId }) {
  const colKey = 'list-sheets:' + projectId
  // Derive collapsed from wf directly — never local state.
  // Parent (ProjectListSection) subscribes to outline-render and re-renders
  // this component whenever wf.collapsed changes (toggle, expand-all, etc.).
  const isCollapsed = wf.collapsed[colKey] !== false
  const [sheets, setSheets] = useState(null)
  const [loading, setLoading] = useState(false)
  // Ref — not state — so flipping it never triggers a render cycle.
  const hasLoaded = useRef(false)

  function maybeLoad() {
    if (hasLoaded.current) return
    hasLoaded.current = true
    setLoading(true)
    getProjectSheets(projectId)
      .then(records => { setSheets(records); setLoading(false) })
      .catch(() => { setSheets([]); setLoading(false) })
  }

  // Fire load whenever the component transitions to expanded — covers:
  //   • user clicks triangle (toggle writes wf.collapsed, dispatches outline-render)
  //   • expand-all from menu (writes wf.collapsed, dispatches outline-render)
  //   • hard refresh with saved expanded state (isCollapsed is false on first render)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (!isCollapsed) maybeLoad() }, [isCollapsed])

  function toggle() {
    wf.collapsed[colKey] = isCollapsed ? false : true
    saveViewState()
    window.dispatchEvent(new Event('outline-render'))
  }

  const groups = sheets ? groupByDiscipline(sheets) : null

  return (
    <div className={`wf-node wf-node--sec-link${isCollapsed ? ' wf-node--collapsed' : ''}`} data-key={colKey}>
      <div className="wf-row">
        <button className="wf-triangle" onClick={toggle}>
          <span className={`wf-tri-icon ${isCollapsed ? 'wf-tri-icon--right' : 'wf-tri-icon--down'}`}></span>
        </button>
        <button
          className="wf-bullet wf-bullet--sec-link"
          title="Open Drawing Sheets"
          data-fn="zoomTo"
          data-level="project-root"
          data-projid={projectId}
          data-rootname="production"
          data-section="sec:prod:sheets"
        ></button>
        <div className="wf-text-wrap">
          <span className="wf-node-text wf-node-text--sec-link">Drawing Sheets</span>
          {sheets && <span className="wf-prog" style={{ color: '#7a9aaa', marginLeft: 6 }}>{sheets.length}</span>}
        </div>
      </div>
      {!isCollapsed && (
        <div className="wf-children">
          {(loading || sheets === null) && <div className="wf-loading">Loading...</div>}
          {sheets && sheets.length === 0 && (
            <div className="wf-loading" style={{ color: '#7a9aaa' }}>No drawing sheets</div>
          )}
          {groups && groups.map(({ discipline, sheets: dSheets }) => (
            <DisciplineNavGroup
              key={discipline}
              discipline={discipline}
              sheets={dSheets}
              projectId={projectId}
            />
          ))}
        </div>
      )}
    </div>
  )
}

/* ── BIM Systems nav item — lazy loads on expand ────────────────────────── */
function BimNavItem({ projectId }) {
  const colKey = 'list-bim:' + projectId
  // Derive collapsed from wf directly — never local state.
  const isCollapsed = wf.collapsed[colKey] !== false
  const [bim, setBim] = useState(null)
  const [loading, setLoading] = useState(false)
  const hasLoaded = useRef(false)

  function maybeLoad() {
    if (hasLoaded.current) return
    hasLoaded.current = true
    setLoading(true)
    getProjectBimSystems(projectId)
      .then(records => { setBim(records); setLoading(false) })
      .catch(() => { setBim([]); setLoading(false) })
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (!isCollapsed) maybeLoad() }, [isCollapsed])

  function toggle() {
    wf.collapsed[colKey] = isCollapsed ? false : true
    saveViewState()
    window.dispatchEvent(new Event('outline-render'))
  }

  function bimColor(status) {
    return status.indexOf('Complete') !== -1 ? '#1D9E75' : status.indexOf('No ') !== -1 ? '#c0392b' : '#7a9aaa'
  }

  return (
    <div className={`wf-node wf-node--sec-link${isCollapsed ? ' wf-node--collapsed' : ''}`} data-key={colKey}>
      <div className="wf-row">
        <button className="wf-triangle" onClick={toggle}>
          <span className={`wf-tri-icon ${isCollapsed ? 'wf-tri-icon--right' : 'wf-tri-icon--down'}`}></span>
        </button>
        <button
          className="wf-bullet wf-bullet--sec-link"
          title="Open BIM Systems"
          data-fn="zoomTo"
          data-level="project-root"
          data-projid={projectId}
          data-rootname="design"
          data-section="sec:design:bim"
        ></button>
        <div className="wf-text-wrap">
          <span className="wf-node-text wf-node-text--sec-link">BIM Systems</span>
          {bim && <span className="wf-prog" style={{ color: '#7a9aaa', marginLeft: 6 }}>{bim.length}</span>}
        </div>
      </div>
      {!isCollapsed && (
        <div className="wf-children">
          {(loading || bim === null) && <div className="wf-loading">Loading...</div>}
          {bim && bim.length === 0 && (
            <div className="wf-loading" style={{ color: '#7a9aaa' }}>No BIM systems</div>
          )}
          {bim && bim.map(b => {
            const status = b.fields['Status'] || ''
            const color = status ? bimColor(status) : null
            return (
              <div key={b.id} className="wf-node wf-node--sec-link">
                <div className="wf-row">
                  <span className="wf-triangle-spacer wf-triangle-spacer--hidden"></span>
                  <button className="wf-bullet wf-bullet--sec-link"></button>
                  <div className="wf-text-wrap">
                    <span className="wf-node-text wf-node-text--sec-link">{b.fields['Name'] || 'BIM System'}</span>
                    {status && <span className="wf-status-pill" style={{ color, borderColor: color + '33' }}>{status}</span>}
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

/* ── Production nav link — expandable with lazy-loaded sheets ───────────── */
function ProductionNavLink({ projectId }) {
  const key = 'production:' + projectId
  const isCollapsed = wf.collapsed[key] !== false

  return (
    <div className={`wf-node wf-node--sec-link${isCollapsed ? ' wf-node--collapsed' : ''}`} data-key={key}>
      <div className="wf-row">
        <button className="wf-triangle" data-fn="toggleCollapse" data-key={key}>
          <span className={`wf-tri-icon ${isCollapsed ? 'wf-tri-icon--right' : 'wf-tri-icon--down'}`}></span>
        </button>
        <button
          className="wf-bullet wf-bullet--sec-link"
          title="Open Production"
          data-fn="zoomTo"
          data-level="project-root"
          data-projid={projectId}
          data-rootname="production"
        ></button>
        <div className="wf-text-wrap">
          <span className="wf-node-text wf-node-text--sec-link">Production</span>
        </div>
        <button
          className="wf-menu-btn"
          data-fn="openMenu"
          data-type="project-nav"
          data-key={key}
          data-nodekey={key}
        >&#8943;</button>
      </div>
      {!isCollapsed && (
        <div className="wf-children">
          <DrawingSheetsNavItem projectId={projectId} />
        </div>
      )}
    </div>
  )
}

/* ── Collapsible Schedule row — uses shared PhaseRow ────────────────────── */
function ScheduleRow({ projectId, phases, milestones, onTimeToggle }) {
  const key = 'sched:' + projectId
  // When a filter is active, force expand so phase children are visible
  const isCollapsed = wf.cmdFilter?.type === 'msIds' ? false : (wf.collapsed[key] !== false)

  return (
    <div className={`wf-node wf-node--sec-link${isCollapsed ? ' wf-node--collapsed' : ''}`} data-key={key}>
      <div className="wf-row">
        <button className="wf-triangle" data-fn="toggleCollapse" data-key={key}>
          <span className={`wf-tri-icon ${isCollapsed ? 'wf-tri-icon--right' : 'wf-tri-icon--down'}`}></span>
        </button>
        <button
          className="wf-bullet wf-bullet--sec-link"
          title="Open Schedule"
          data-fn="zoomTo"
          data-level="project-root"
          data-projid={projectId}
          data-rootname="schedule"
        ></button>
        <div className="wf-text-wrap">
          <span className="wf-node-text wf-node-text--sec-link">Schedule</span>
        </div>
        <button
          className="wf-menu-btn"
          data-fn="openMenu"
          data-type="project-nav"
          data-key={key}
          data-nodekey={key}
        >&#8943;</button>
      </div>
      {!isCollapsed && (
        <div className="wf-children">
          {(phases === null || phases === undefined) && (
            <div className="wf-loading">Loading...</div>
          )}
          {phases && phases.length === 0 && (
            <div className="wf-loading" style={{ color: '#7a9aaa' }}>No phases</div>
          )}
          {phases && phases.length > 0 && phases.map(ph => (
            <PhaseRow
              key={ph.id}
              ph={ph}
              milestones={milestones || []}
              onTimeToggle={onTimeToggle}
              projId={projectId}
            />
          ))}
        </div>
      )}
    </div>
  )
}

// Project ids whose home-row background load is queued or running (survives row remounts).
const rowLoadsInFlight = new Set()

function ProjectRow({ proj }) {
  const projectId = proj.id
  const key = 'proj:' + projectId

  // ── Load phases and milestones only while a command-bar milestone filter is on ──
  // The row always draws collapsed ("Open the project to expand the outline") and never shows
  // its phases, so the only reader of this data is the filter below. It used to load whenever
  // wf.collapsed[key] was false — after an "Expand all", every visit home fetched two reads per
  // project (44 projects = ~25s of requests) that nothing displayed, and opening a project waited
  // behind them. hasLoaded is a ref so it never triggers a re-render.
  const hasLoaded = useRef(false)

  const cmdFilter = wf.cmdFilter
  const filterOn = cmdFilter?.type === 'msIds'

  function maybeLoad() {
    if (hasLoaded.current) return
    // If the outline engine already started or finished loading this project, skip
    if (ctx.phasesCache && ctx.phasesCache[projectId] !== undefined) {
      hasLoaded.current = true
      return
    }
    hasLoaded.current = true
    if (rowLoadsInFlight.has(projectId)) return
    rowLoadsInFlight.add(projectId)
    // A background load: low priority in the request line, and it does NOT claim the shared
    // null "in flight" sentinel. Opening this project while the load still waits must start the
    // outline engine's own load, not sit behind every other expanded row's (see services/_api.js).
    // It only fills the caches if nothing else has filled them meanwhile.
    const fill = (phases, milestones) => {
      rowLoadsInFlight.delete(projectId)
      if (ctx.phasesCache && ctx.phasesCache[projectId] === undefined) {
        ctx.phasesCache[projectId] = phases
        if (ctx.milestonesCache) ctx.milestonesCache[projectId] = milestones
      }
      window.dispatchEvent(new Event('outline-render'))
    }
    Promise.all([
      getProjectPhases(projectId, { low: true }),
      getProjectMilestones(projectId, { low: true }),
    ]).then(([phases, milestones]) => fill(phases, milestones))
      .catch(() => fill([], []))
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (filterOn) maybeLoad() }, [filterOn])

  const phases     = ctx.phasesCache?.[projectId]
  const milestones = ctx.milestonesCache?.[projectId]

  // Command filter: hide projects with no matching milestones (once milestones are loaded)
  if (cmdFilter?.type === 'msIds' && milestones) {
    if (!milestones.some(m => cmdFilter.msIds.has(m.id))) return null
  }

  const name    = proj.fields['Project Name'] || 'Unnamed Project'
  const number  = proj.fields['Project Number'] || ''
  const nextDue = fmtDue(proj.fields['Next Deliverable Due'])
  const onHold  = !!(ctx.projectHoldIds && ctx.projectHoldIds.has(projectId))
  // Closed Projects view (Close Out Project — docs/PROJECT_CLOSEOUT.md): every row here already
  // has Contract Stage 'Completed' or 'Cancelled' — read straight off the record, no extra fetch.
  const closedStage = ctx.showClosedProjects ? (proj.fields['Contract Stage'] || '') : ''

  function handleTimeToggle(phId, currentValue) {
    if (ctx.phasesCache?.[projectId]) {
      const ph = ctx.phasesCache[projectId].find(p => p.id === phId)
      if (ph) ph.fields['Allow Time Entries'] = !currentValue
    }
    window.dispatchEvent(new Event('outline-render'))
    updatePhaseTimeEntries(phId, !currentValue)
  }

  return (
    <div className="wf-node wf-node--project wf-node--collapsed" data-key={key}>
      <div className="wf-row" onClick={e => {
        if (e.ctrlKey || e.metaKey) { e.preventDefault(); e.stopPropagation(); window.open('/project?recordId=' + projectId, '_blank') }
      }}>
        <button
          className="wf-triangle wf-triangle--hint"
          title="Open the project to expand the outline"
          onClick={e => e.stopPropagation()}
        >
          <span className="wf-tri-icon wf-tri-icon--right"></span>
        </button>
        <button
          className="wf-bullet wf-bullet--project"
          title="Open project"
          data-fn="zoomTo"
          data-level="project"
          data-projid={projectId}
        ></button>
        <div className="wf-text-wrap">
          <span className="wf-node-text wf-node-text--project">
            {number && <span className="wf-proj-number">{number}</span>}
            {name}
          </span>
          <span className="wf-meta">
            {onHold && (
              <span style={{
                fontFamily: '"Montserrat",sans-serif', fontSize: 10, fontWeight: 600, letterSpacing: '0.08em',
                textTransform: 'uppercase', color: '#b45309', background: 'rgba(180,83,9,0.12)',
                borderRadius: 10, padding: '2px 8px', marginRight: 8,
              }} title="Project on hold — billing, progress, and scheduling are paused">On hold</span>
            )}
            {closedStage && (
              <span style={{
                fontFamily: '"Montserrat",sans-serif', fontSize: 10, fontWeight: 600, letterSpacing: '0.08em',
                textTransform: 'uppercase',
                color: closedStage === 'Completed' ? '#1a7a4a' : '#6b7280',
                background: closedStage === 'Completed' ? 'rgba(26,122,74,0.12)' : 'rgba(107,114,128,0.14)',
                borderRadius: 10, padding: '2px 8px', marginRight: 8,
              }}>{closedStage}</span>
            )}
            {nextDue && (
              <span className="wf-prog" style={{ color: '#7a9aaa', fontFamily: 'DM Mono, monospace' }}>{nextDue}</span>
            )}
          </span>
        </div>
        <button
          className="wf-menu-btn"
          data-fn="openMenu"
          data-type="project"
          data-key={key}
          data-projid={projectId}
        >&#8943;</button>
      </div>
    </div>
  )
}

export default function ProjectListSection() {
  const [, forceUpdate] = useReducer(v => v + 1, 0)
  const holdIdsLoaded = useRef(false)

  useEffect(() => {
    function onRender() { forceUpdate() }
    window.addEventListener('outline-render', onRender)
    return () => window.removeEventListener('outline-render', onRender)
  }, [])

  // One-time fetch, not per-row — powers the on-hold badge on ProjectRow via ctx.projectHoldIds.
  useEffect(() => {
    if (holdIdsLoaded.current) return
    holdIdsLoaded.current = true
    getActiveProjectHoldIds().then(ids => { ctx.projectHoldIds = new Set(ids); forceUpdate() }).catch(() => {})
  }, [])

  /* Only show at the project list level — hide when zoomed into a project */
  if (wf.zoomProjectId) return null

  // "Show closed projects" toggle (Close Out Project — docs/PROJECT_CLOSEOUT.md): Completed/
  // Cancelled projects are otherwise invisible everywhere (home list, search, /go) once Contract
  // Stage leaves 'Executed' — this toggle, flipped from the Projects ⋯ menu, is the one place they
  // stay findable. ctx.closedProjectsCache: undefined = not fetched yet, null = in-flight, [...] = loaded.
  const showClosed = !!ctx.showClosedProjects
  const rawProjects = showClosed ? ctx.closedProjectsCache : ctx.projectsCache

  // "Show/hide on-hold projects" toggle: defaults to hidden — a project put on hold is paused
  // work, not part of the everyday list. Pure client-side filter, since getOpenProjects() already
  // fetches Executed + On Hold together into ctx.projectsCache (see docs/PROJECT_HOLD.md).
  const showOnHold = !!ctx.showOnHoldProjects
  const activeProjects = (!showClosed && !showOnHold && Array.isArray(rawProjects) && ctx.projectHoldIds)
    ? rawProjects.filter(p => !ctx.projectHoldIds.has(p.id))
    : rawProjects

  // Project scope (docs/FLOW_RECONCILIATION_PLAN.md, decision 10): the default list is only the
  // projects with a flight the logged-in person is crewed on — their flights sit pinned above the
  // outline, and this is the outline those flights belong to. "All projects" is one click away on
  // the title line and persists. Closed-projects view is its own thing and ignores the scope.
  const scope = getProjectScope()
  const scopeMine = scope === 'mine' && !showClosed
  const memberId = wf.currentMemberId
  const myFlights = scopeMine && memberId ? flightsForMember(memberId) : []
  const myProjectIds = new Set(myFlights.map(f => f.projectId).filter(Boolean))
  const scopeReady = !scopeMine || (memberId && crewIndexReady())
  const projects = (scopeMine && Array.isArray(activeProjects))
    ? (scopeReady ? activeProjects.filter(p => myProjectIds.has(p.id)) : null)
    : activeProjects
  // A flight whose project is not in the active list (held, or closed since launch) still
  // belongs to this person — say so instead of silently showing fewer projects than flights.
  const orphanFlights = (scopeMine && Array.isArray(activeProjects) && scopeReady)
    ? myFlights.filter(f => f.projectId && !activeProjects.some(p => p.id === f.projectId))
    : []

  /* Home reads as a zoom into "Projects": ZoomTitle supplies the title line
     ("Projects" + count + the My projects / All projects toggle + ⋯ menu); project rows
     render at top level (children-only — no root:projects wrapper node). */
  return (
    <div className="wf-list">
      {(projects === null || projects === undefined) && <div className="wf-loading">Loading projects...</div>}
      {projects && projects.length === 0 && !scopeMine && (
        <div className="wf-loading" style={{ color: '#7a9aaa' }}>{showClosed ? 'No closed projects' : 'No active projects'}</div>
      )}
      {projects && projects.length === 0 && scopeMine && (
        <div className="wf-loading" style={{ color: 'var(--aj-text-muted)', lineHeight: 1.6 }}>
          {crewIndexFailed()
            ? 'Your flights couldn’t be loaded just now, so this list is empty — retrying.'
            : myFlights.length === 0
              ? "You're not crewed on anything right now, so there are no projects to show here."
              : 'Your flights are on projects that are not in the active list.'}
          {' '}
          <button
            type="button"
            onClick={() => setProjectScope('all')}
            style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: 'var(--aj-accent)', font: 'inherit', fontWeight: 600, textDecoration: 'underline', textUnderlineOffset: 2 }}
          >Show all projects</button>
        </div>
      )}
      {projects && projects.map(proj => (
        <ProjectRow key={proj.id} proj={proj} />
      ))}
      {orphanFlights.length > 0 && projects && projects.length > 0 && (
        <div className="wf-loading" style={{ color: 'var(--aj-text-muted)', fontSize: 12 }}>
          {orphanFlights.length === 1 ? 'One of your flights is' : orphanFlights.length + ' of your flights are'} on a project outside the active list.
        </div>
      )}
    </div>
  )
}
